/* eslint-disable react/prop-types */
import { FC, useEffect, useId, useRef, useState } from "react";
import { X, Search } from "lucide-react";
import { useAvatar } from "src/hooks";
import cmsClient from "src/services/cmsClient";

interface UserOption {
	id: string;
	name: string;
	role: string;
}

interface Props {
	value: string;
	onChange: (authorId: string) => void;
}

const AuthorResultItem: FC<{ user: UserOption }> = ({ user }) => {
	const { data: avatarSrc, isError } = useAvatar({ userId: user.id });

	const initials = user.name
		? user.name
				.split(" ")
				.slice(0, 2)
				.map((n) => n[0])
				.join("")
				.toUpperCase()
		: "";

	return (
		<div className="flex items-center gap-3">
			<div
				className="flex-shrink-0 w-9 h-9 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center text-xs font-medium overflow-hidden"
				aria-hidden="true"
			>
				{!isError && avatarSrc ? (
					<img src={avatarSrc} alt="" className="w-full h-full object-cover" />
				) : (
					<span>{initials}</span>
				)}
			</div>
			<div className="flex flex-col min-w-0">
				<span className="text-sm font-medium text-foreground block truncate">
					{user.name}
				</span>
				{user.role && (
					<span className="text-[13px] text-muted-foreground truncate">
						{user.role}
					</span>
				)}
			</div>
		</div>
	);
};

const AuthorFilter: FC<Props> = ({ value, onChange }) => {
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [users, setUsers] = useState<UserOption[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedName, setSelectedName] = useState("");
	const containerRef = useRef<HTMLDivElement>(null);
	const listboxId = useId();

	// Debounce search input
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	// Click outside to close dropdown
	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	// Fetch users on debounced search
	useEffect(() => {
		if (!debouncedSearch || debouncedSearch.length < 2) {
			setUsers([]);
			return;
		}
		let cancelled = false;
		cmsClient
			.get<
				Array<{
					username: string;
					full_name: string;
					role?: string | null;
				}>
			>("/socialprofile/search/", {
				params: { search_term: debouncedSearch }
			})
			.then((res) => {
				if (cancelled) return;
				setUsers(
					res.data.map((u) => ({
						id: u.username,
						name: u.full_name || u.username,
						role: u.role ?? ""
					}))
				);
			})
			.catch(() => {
				if (!cancelled) setUsers([]);
			});
		return () => {
			cancelled = true;
		};
	}, [debouncedSearch]);

	const handleSelect = (user: UserOption) => {
		onChange(user.id);
		setSelectedName(user.name);
		setSearch("");
		setIsOpen(false);
	};

	const handleClear = () => {
		onChange("");
		setSelectedName("");
		setSearch("");
		setIsOpen(false);
	};

	return (
		<div className="relative" ref={containerRef}>
			<label
				htmlFor="filter-author"
				className="block text-sm font-medium text-zinc-600 mb-1.5"
			>
				Author
			</label>
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
				{value ? (
					<div className="flex items-center w-full bg-white border border-border rounded-lg pl-9 pr-9 py-2 text-base">
						<span className="text-foreground truncate">{selectedName}</span>
						<button
							type="button"
							aria-label="Clear author"
							className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted transition-colors"
							onClick={handleClear}
						>
							<X className="h-4 w-4 text-zinc-500" />
						</button>
					</div>
				) : (
					<>
						<input
							id="filter-author"
							type="text"
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setIsOpen(true);
							}}
							onFocus={() => search.length >= 2 && setIsOpen(true)}
							placeholder="Search by author..."
							role="combobox"
							aria-expanded={isOpen && users.length > 0}
							aria-autocomplete="list"
							aria-controls={listboxId}
							className="w-full bg-white border border-border rounded-lg pl-9 pr-9 py-2 text-base shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
						/>
						{search && (
							<button
								type="button"
								aria-label="Clear search"
								onClick={() => {
									setSearch("");
									setDebouncedSearch("");
									setUsers([]);
									setIsOpen(false);
								}}
								className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-500 hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
							>
								<X className="h-4 w-4" aria-hidden="true" />
							</button>
						)}
					</>
				)}

				{isOpen && users.length > 0 && !value && (
					<div
						id={listboxId}
						role="listbox"
						className="absolute top-full left-0 z-10 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto w-full mt-1"
					>
						{users.map((user) => (
							<button
								key={user.id}
								type="button"
								role="option"
								aria-selected={false}
								className="w-full text-left px-3 py-2.5 hover:bg-primary-lighter transition-colors first:rounded-t-lg last:rounded-b-lg"
								onClick={() => handleSelect(user)}
							>
								<AuthorResultItem user={user} />
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default AuthorFilter;
